import { DataReportUsecase } from "./data_report"
// import { API } from "@/api" // /instance/wanip 暂不启用

interface ProbeResult {
  destIP: string
  sent: number
  received: number
  lossRate: number
  avgRTT: number
}
const DETECTION_INTERVAL_MS = 60_000
const DETECTION_DOMAIN = "www.baidu.com"
// Match Android's current _probe invocation: 20 probes, 2.5 seconds apart.
const PROBE_PACKET_COUNT = 20
const PROBE_INTERVAL_MS = 2_500
let timer: number | null = null
let running = false
let detecting = false
let detectionGeneration = 0

async function probe(host: string): Promise<ProbeResult> {
  let received = 0
  let totalRtt = 0
  const sent = PROBE_PACKET_COUNT
  for (let i = 0; i < sent; i++) {
    const started = performance.now()
    let timeout: number | null = null
    try {
      const controller = new AbortController()
      timeout = window.setTimeout(() => controller.abort(), 900)
      await fetch(`https://${host}/?kcdesk_probe=${Date.now()}_${i}`, {
        mode: "no-cors",
        cache: "no-store",
        signal: controller.signal,
      })
      received++
      totalRtt += performance.now() - started
    } catch (_) {
      // A browser cannot send ICMP. This is an HTTPS availability probe only.
    } finally {
      if (timeout !== null) window.clearTimeout(timeout)
    }
    // Android uses ping -c 20 -i 2.5. Keep adjacent HTTP probe starts 2.5 seconds
    // apart, including failed requests, as the browser-compatible equivalent.
    if (i < sent - 1) {
      const waitMs = Math.max(
        0,
        PROBE_INTERVAL_MS - (performance.now() - started)
      )
      await new Promise((resolve) => window.setTimeout(resolve, waitMs))
    }
  }
  return {
    destIP: host,
    sent,
    received,
    lossRate: sent ? ((sent - received) / sent) * 100 : 0,
    avgRTT: received ? Math.round(totalRtt / received) : 0,
  }
}

async function runCycle(): Promise<void> {
  if (!detecting || !navigator.onLine || running) return
  running = true
  const generation = detectionGeneration
  try {
    const result = await probe(DETECTION_DOMAIN)
    // const wanIp = await API.getWanIp()

    if (!detecting || generation !== detectionGeneration) return

    const extra: Record<string, unknown> = {
      // Keep this explicit so downstream consumers do not treat HTTP failures
      // as ICMP packet loss. Browsers cannot expose DNS resolution results,
      // therefore dest_ip and NAT gateway fields are intentionally omitted.
      domain: DETECTION_DOMAIN,
      sent: result.sent,
      received: result.received,
      lossRate: result.lossRate,
      avgRTT: result.avgRTT,
    }
    // if (typeof wanIp === "string") extra.wan_ip = wanIp
    // 上报与探测解耦：不 await，避免 getLocalDeviceId/HTTP 拖长 running 窗口、影响下一轮调度。
    void DataReportUsecase.reportLog("client_detection_domain", extra)
  } finally {
    running = false
  }
}

function onNetworkOnline(): void {
  void runCycle()
}

// 固定周期调度：与 Android 端 Timer.periodic(Duration(minutes:1)) 对齐。
// runCycle 以 fire-and-forget 方式触发，不阻塞下一轮调度；
// 单轮 probe 耗时(<60s)不会叠加进周期，上报稳定为每 60s 一次。
// running 守卫保证：若上一轮 probe 未结束（耗时偶发超 60s），跳过本次触发，避免重叠堆积。
function scheduleNext(): void {
  if (!detecting) return
  timer = window.setInterval(() => {
    void runCycle().catch(console.error)
  }, DETECTION_INTERVAL_MS)
}

export const NetworkDetectionUsecase = {
  start(): void {
    if (detecting) return
    detecting = true
    window.addEventListener("online", onNetworkOnline)
    // 立即跑一轮，并启动固定周期定时器（对齐 Android Timer.periodic）。
    // runCycle 不阻塞定时器，单轮耗时 < 间隔时上报周期稳定为 60s。
    void runCycle().catch(console.error)
    scheduleNext()
  },
  stop(): void {
    if (!detecting) return
    detecting = false
    detectionGeneration++
    window.removeEventListener("online", onNetworkOnline)
    if (timer !== null) window.clearInterval(timer)
    timer = null
  },
}
